import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { NestedItem, SaltShellNavigation } from "@/components/dashboard/salt-shell-navigation";
import { SaltPageChrome } from "@/components/dashboard/salt-page-chrome";
import {
  PageTitleOverrideProvider,
  usePageTitleController,
} from "@/components/dashboard/page-title-override";
import { Drawer, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { FlexLayout, SaltIcons, StackLayout } from "@/design-system/salt";
import { useAuth } from "@/hooks/useAuth";
import { useIsAtLeast } from "@/hooks/useIsAtLeast";
import {
  getIssuerHubActiveTab,
  getIssuerHubPageTitle,
  issuerHubNavigation,
  type IssuerHubNavItem,
} from "./issuer-hub-navigation";

interface IssuerHubLayoutProps {
  children: React.ReactNode;
}

/** Standalone issuer workspace using the same Salt shell contract as the main app. */
export default function IssuerHubLayout({ children }: IssuerHubLayoutProps) {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [chromeSearch, setChromeSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const handleChromeSearchSubmit = useCallback(
    (value: string) => {
      const query = value.trim();
      navigate(query ? `/corporates?search=${encodeURIComponent(query)}` : "/corporates");
    },
    [navigate],
  );

  const activeTab = useMemo(() => getIssuerHubActiveTab(location.pathname), [location.pathname]);
  // Same contract as DashboardLayout: the route table names the page unless the page
  // publishes something only it knows. /issuer-hub/prospecting needs that — it swaps
  // between a list, a create form and an edit form on local state, at one URL.
  const { override: pageTitleOverride, setOverride: setPageTitleOverride } =
    usePageTitleController();
  const routeTitle = useMemo(() => getIssuerHubPageTitle(location.pathname), [location.pathname]);
  const pageTitle = pageTitleOverride ?? routeTitle;

  void activeTab;

  const closeNav = useCallback(() => setNavigationOpen(false), []);

  const renderNavItem = useCallback(
    (item: IssuerHubNavItem) => <NestedItem item={item} key={item.id} onNavigate={closeNav} />,
    [closeNav],
  );

  const issuerHubNavItems: readonly IssuerHubNavItem[] = useMemo(
    () => [
      ...issuerHubNavigation,
      {
        id: "back-to-app",
        name: "Back to app",
        path: "/action-center",
        icon: SaltIcons.ChevronLeftIcon,
      },
    ],
    [],
  );

  const shellNavigation = (
    <SaltShellNavigation
      homePath="/issuer-hub/pipeline"
      initials="IH"
      navItems={issuerHubNavItems}
      onNavigate={closeNav}
      onSignOut={handleSignOut}
      subtitle="Issuer workspace"
      title="Issuer Hub"
    />
  );

  const isDesktop = useIsAtLeast("lg");

  // `height`, not `minHeight`, on the shell below. This is a fixed-viewport app frame:
  // the content column clips and `<main>` scrolls inside it. A `min-height` leaves the
  // shell's height indefinite, and an indefinite column takes a `flex: 1 1 0%` child's
  // max-content contribution — so `<main>` sized itself to its content, the column grew
  // past the viewport, and the `overflow: auto` on main never engaged. Measured at
  // 1440x400 on /issuer-hub/pipeline: with `minHeight` the shell was 647px tall, main
  // 594px, `main.scrollHeight === main.clientHeight`, and the DOCUMENT scrolled 263px
  // (taking the chrome away with it). With `height` the shell is 400px, main 347px, and
  // main scrolls. Re-measured at 400/600/900px tall: the sidebar does not spill (its nav
  // list already carries `flex: 1` + `overflow-y: auto`), and document overflow stays at
  // the 16px every route in this app already has from the unreset 8px `body` margin.
  return (
    <FlexLayout
      gap={0}
      style={{ height: "100vh", background: "var(--salt-container-secondary-background)" }}
    >
      {/* Rendered conditionally rather than CSS-gated so the Drawer below is the only
          navigation mounted on narrow viewports. `width` was previously the string
          "250" — React only appends px to numbers, so the browser dropped it. */}
      {isDesktop && (
        <aside
          style={{
            width: 250,
            flexShrink: 0,
            borderRight: "var(--salt-size-border) solid var(--salt-separable-primary-borderColor)",
          }}
        >
          {shellNavigation}
        </aside>
      )}

      <Drawer
        style={{ padding: "0px" }}
        onOpenChange={setNavigationOpen}
        open={navigationOpen}
        position="left"
      >
        <DrawerHeader className="sr-only">
          <DrawerTitle>Issuer Hub navigation</DrawerTitle>
          <DrawerDescription>Navigate the issuer workspace.</DrawerDescription>
        </DrawerHeader>
        {shellNavigation}
      </Drawer>

      {/* `gap={0}` is not decoration. Salt's StackLayout (and the FlexLayout above)
          default to `gap = 3`, emitted as `calc(var(--salt-spacing-100) * 3)` — 24px
          at medium density. Measured at 1440x900 before this prop existed: the chrome
          ended at y=81 and `<main>` began at y=105, so every Issuer Hub route showed a
          24px band of bare page background under its own header, and the sidebar border
          at x=258 was separated from the content column at x=282 by a second one.
          `DashboardLayout` sets `gap={0}` on the identical chrome/main pair. */}
      <StackLayout
        direction="column"
        gap={0}
        style={{ flex: "1 1 0%", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
      >
        <SaltPageChrome
          onMenu={() => setNavigationOpen(true)}
          onSearchChange={setChromeSearch}
          onSearchSubmit={handleChromeSearchSubmit}
          searchValue={chromeSearch}
          showSearch={false}
          title={pageTitle}
        />

        {/* `flex: "1 1 0%"` is the other half of what makes the declared `overflow: auto`
            mean anything: it hands main the height the chrome leaves. Without it main was
            content-sized — measured 594px tall inside a 900px column, so 209px of bare
            page background sat below the content — and it needs the definite shell height
            above to do anything at all. `minHeight: 0` keeps a flex item from refusing to
            shrink below its content, which is the usual way a scroll container silently
            collapses back into a block. */}
        <main
          style={{
            flex: "1 1 0%",
            minHeight: 0,
            overflow: "auto",
            background: "var(--salt-container-secondary-background)",
          }}
        >
          {/*
            No hidden `<h1>` here any more. This layout used to supply one — visually
            hidden, and only on the routes whose page rendered none — because the chrome
            title was deliberately NOT a heading, so without it `/issuer-hub/pipeline` and
            `/issuer-hub/campaigns` had no `<h1>` at all and axe reported
            `page-has-heading-one` on both.

            Aaron reversed that on 2026-08-18: the chrome title IS the `<h1>` now, on this
            shell and the main one alike. So all five routes get a real, VISIBLE heading
            from `SaltPageChrome` above, and a hidden one here would be a second — the
            duplicate this block's conditional existed to prevent. The
            `issuerHubPageSuppliesOwnHeading` list it depended on is gone with it: the
            three pages that rendered their own heading had it removed in the same sweep,
            so there is no longer a per-route question to answer.
          */}
          {/* Page gutter via the Salt prop, never `className="spacing-page"` — a
              layered app class cannot beat Salt's unlayered
              `.saltFlexLayout { padding: var(--flexLayout-padding) }`, and
              StackLayout always writes that custom property (padding defaults to
              0). The class was inert here, which is why every Issuer Hub route
              rendered flush to all four edges. See DR-28. */}
          <StackLayout
            direction="column"
            padding="var(--salt-layout-page-margin)"
            style={{
              maxWidth: "100%",
              paddingBottom:
                "calc(var(--salt-layout-page-margin) + env(safe-area-inset-bottom, 0px))",
            }}
          >
            <PageTitleOverrideProvider value={setPageTitleOverride}>
              {children}
            </PageTitleOverrideProvider>
          </StackLayout>
        </main>
      </StackLayout>
    </FlexLayout>
  );
}
